import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

interface BookerCalendarProps {
  selectedDate: string | null;
  onChange: (dateStr: string) => void;
  minDate?: Date;
  maxDate?: Date;
  availableDates?: Set<string>;
  brandColor?: string;
}

function toDateString(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function BookerCalendar({
  selectedDate,
  onChange,
  minDate,
  maxDate,
  availableDates,
  brandColor = '#292929',
}: BookerCalendarProps) {
  const today = useMemo(() => toDateString(new Date()), []);
  const [viewYear, setViewYear] = useState(() => {
    if (selectedDate) return parseInt(selectedDate.slice(0, 4));
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    if (selectedDate) return parseInt(selectedDate.slice(5, 7)) - 1;
    return new Date().getMonth();
  });

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(y => y - 1);
    } else {
      setViewMonth(m => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(y => y + 1);
    } else {
      setViewMonth(m => m + 1);
    }
  };

  // Can go prev?
  const canGoPrev = useMemo(() => {
    if (!minDate) return true;
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    return firstOfMonth > minMonth;
  }, [viewYear, viewMonth, minDate]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    // Day of week: 0=Sun..6=Sat -> convert to Mon=0..Sun=6
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const days: Array<{ date: string; day: number; disabled: boolean; isToday: boolean } | null> = [];

    // Leading empty cells
    for (let i = 0; i < startDow; i++) {
      days.push(null);
    }

    const minStr = minDate ? toDateString(minDate) : null;
    const maxStr = maxDate ? toDateString(maxDate) : null;

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      let disabled = false;

      if (minStr && dateStr < minStr) disabled = true;
      if (maxStr && dateStr > maxStr) disabled = true;
      if (availableDates && !availableDates.has(dateStr)) disabled = true;

      days.push({
        date: dateStr,
        day: d,
        disabled,
        isToday: dateStr === today,
      });
    }

    return days;
  }, [viewYear, viewMonth, minDate, maxDate, availableDates, today]);

  return (
    <div className="w-full">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goPrevMonth}
          disabled={!canGoPrev}
          className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-sm font-semibold text-gray-900">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h2>
        <button
          onClick={goNextMonth}
          className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map(label => (
          <div
            key={label}
            className="text-center text-xs font-medium text-gray-500 uppercase tracking-widest py-1.5"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((dayInfo, i) => {
          if (!dayInfo) {
            return <div key={`empty-${i}`} className="w-full aspect-square" />;
          }

          const isSelected = dayInfo.date === selectedDate;

          return (
            <button
              key={dayInfo.date}
              onClick={() => !dayInfo.disabled && onChange(dayInfo.date)}
              disabled={dayInfo.disabled}
              className={`
                relative w-full aspect-square flex flex-col items-center justify-center
                rounded-md text-sm font-medium transition-all
                ${dayInfo.disabled
                  ? 'text-gray-300 cursor-default font-light'
                  : isSelected
                    ? 'text-white'
                    : 'text-gray-900 bg-gray-100 hover:border-2'
                }
              `}
              style={
                dayInfo.disabled
                  ? undefined
                  : isSelected
                    ? { backgroundColor: brandColor }
                    : { borderColor: 'transparent' }
              }
              onMouseEnter={(e) => {
                if (!dayInfo.disabled && !isSelected) {
                  (e.currentTarget as HTMLElement).style.borderColor = brandColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!dayInfo.disabled && !isSelected) {
                  (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
                }
              }}
            >
              {dayInfo.day}
              {/* Today dot */}
              {dayInfo.isToday && (
                <span
                  className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: isSelected ? '#fff' : brandColor }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
